import { cn, Label, TypographyPUI, TypographySubtle } from '@seen/ui'
import { type JSX, type ReactNode, useId } from 'react'
import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { RadioGroup } from '#src/components/core/radio-group/RadioGroup.tsx'
import { RadioGroupItem } from '#src/components/core/radio-group/RadioGroupItem.tsx'
import { SuggestionSourceExcerpt } from '#src/components/rich-text-editor/SuggestionSourceExcerpt.tsx'

import { ValidatedToolFieldControls } from '#src/components/validated-tool-form/shared/ValidatedToolFieldControls.tsx'
import type { ValidatedToolFieldSuggestion } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
export interface ValidatedToolRadioGroupFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  options: {
    value: FieldPathValue<TFieldValues, TName>
    label: ReactNode
    description?: ReactNode
  }[]
  className?: string
  /**
   * Optional AI suggestion for THIS field. Absent for a field the AI didn't
   * answer — in which case this component renders exactly as it did before (no
   * highlight, no controls, no excerpt).
   */
  suggestion?: ValidatedToolFieldSuggestion
}

export function ValidatedToolRadioGroupField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  options,
  className,
  suggestion,
}: ValidatedToolRadioGroupFieldProps<TFieldValues, TName>): JSX.Element {
  const id = useId()

  // Only a still-pending suggestion tints its option; a decided field (accepted
  // seeded the value, rejected blanked it) renders as a plain option list under
  // the marker.
  const showOverlay = suggestion?.status === 'pending'

  return (
    <FormField
      control={control}
      name={name}
      render={({ field: { value, ...rest }, fieldState }) => (
        <div>
          <RadioGroup
            className={cn(
              fieldState.error && 'rounded-md p-2 ring-1 ring-destructive',
              className
            )}
            value={value}
            {...rest}
          >
            {options.map(({ value, label, description }) => {
              /* The option the AI picked, in EVERY state — pending, accepted or
               * rejected. It anchors the controls, so they neither move at the
               * moment of decision nor leave the decided state unable to say
               * which option it refers to. Only the tint is pending-only. */
              const isAnchor = suggestion?.value === value
              const isSuggested = showOverlay && isAnchor
              return (
                <div key={value}>
                  <Label
                    htmlFor={`${id}-${value}`}
                    className={cn(
                      'grid items-center py-2',
                      /* The tint bleeds 8px OUTWARD (-mx-2 against px-2) rather
                       * than indenting its contents, so the radio column stays
                       * flush with the options that carry no suggestion. */
                      isSuggested &&
                        '-mx-2 rounded-md bg-green-100 px-2 ring-1 ring-green-500/40 ring-inset dark:bg-green-900/30'
                    )}
                    unstyled
                    {...(isSuggested && {
                      'data-vt-suggestion-linkid': name,
                    })}
                  >
                    <div className="flex gap-2">
                      <RadioGroupItem
                        className="mt-[0.295rem]" // Arbitrary value to align the radio button with the text
                        id={`${id}-${value}`}
                        value={value}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <TypographyPUI className="font-semibold">
                            {label}
                          </TypographyPUI>
                        </div>
                        <TypographySubtle className="text-foreground-secondary">
                          {description}
                        </TypographySubtle>
                      </div>
                    </div>
                  </Label>
                  {suggestion && isAnchor && (
                    <ValidatedToolFieldControls
                      linkId={name}
                      suggestion={suggestion}
                    />
                  )}
                  {/* With the controls, directly under the suggested option, so
                      the evidence sits beside the thing it argues for rather
                      than after options it has nothing to say about. OUTSIDE
                      the tint: an accept writes the option's value and discards
                      the excerpt, so a citation inside the highlight would read
                      as part of what is about to be written. */}
                  {isSuggested && (
                    <SuggestionSourceExcerpt
                      citation={suggestion.citation}
                      sourceProvenance={suggestion.sourceProvenance}
                    />
                  )}
                </div>
              )
            })}
          </RadioGroup>
          {fieldState.error?.message && (
            <p className="mt-1 text-sm text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  )
}
